import { ThemeEvents } from '@theme/events';

const BYOB_SELECTOR = 'byob-bundle';
const MOUNT_SELECTOR = '[data-topeak-simple-bundle]';
const SECTION_SELECTOR = '.topeak-product-hero';
const STANDARD_BUY_SELECTOR = '.topeak-product-hero__standard-buy';
const HERO_ADD_LOADING_CLASS = 'topeak-btn--loading';

/** @type {WeakMap<HTMLElement, { selections: Record<string, number>, bundleQty: number, config: BundleConfig }>} */
const bundleStateBySection = new WeakMap();

/**
 * @typedef {Object} BundleProduct
 * @property {string} variantId
 * @property {string} hash
 * @property {string} title
 * @property {string} imageUrl
 * @property {number | null} inventory
 * @property {boolean} available
 */

/**
 * @typedef {Object} BundleConfig
 * @property {string} bundleVariantId
 * @property {string} bundleTitle
 * @property {number} itemCount
 * @property {BundleProduct[]} products
 */

/**
 * @param {number} cents
 * @param {string | undefined} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const safeCents = Math.max(0, Number(cents) || 0);
  const amount = (safeCents / 100).toFixed(2);
  const amountNoDecimals = String(Math.round(safeCents / 100));
  const amountParts = amount.split('.');
  amountParts[0] = amountParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const amountWithComma = amountParts.join('.');

  if (!format) {
    return amount;
  }

  return format
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
    .replace(/\{\{\s*amount\s*\}\}/g, amount);
}

/**
 * @param {Element} byobEl
 * @returns {BundleConfig | null}
 */
function parseByobBundle(byobEl) {
  const bundleVariantId = byobEl.getAttribute('selected-variant-id');
  const packEl = byobEl.querySelector('byob-pack[active], byob-pack');
  const itemCount = Number.parseInt(packEl?.getAttribute('item-count') || '0', 10);

  if (!bundleVariantId || !itemCount) {
    return null;
  }

  /** @type {BundleProduct[]} */
  const products = [];

  for (const productEl of byobEl.querySelectorAll('byob-product')) {
    const variantEl = productEl.querySelector('byob-variant');

    if (!variantEl) {
      continue;
    }

    const inventoryAttr = variantEl.getAttribute('inventory-quantity');
    const inventory = inventoryAttr === null || inventoryAttr === '' ? null : Number.parseInt(inventoryAttr, 10);

    products.push({
      variantId: variantEl.getAttribute('variant-id') || '',
      hash: variantEl.getAttribute('hash') || '',
      title: variantEl.getAttribute('product-title') || productEl.getAttribute('product-title') || '',
      imageUrl: variantEl.getAttribute('image-url') || productEl.getAttribute('image-url') || '',
      inventory: Number.isFinite(inventory) ? inventory : null,
      available: variantEl.getAttribute('available') !== 'false',
    });
  }

  if (!products.length) {
    return null;
  }

  return {
    bundleVariantId,
    bundleTitle: byobEl.getAttribute('product-title') || '',
    itemCount,
    products,
  };
}

/**
 * @param {Record<string, number>} selections
 * @returns {number}
 */
function getSelectedTotal(selections) {
  let total = 0;

  for (const qty of Object.values(selections)) {
    total += qty;
  }

  return total;
}

/**
 * @param {BundleProduct} product
 * @param {number} bundleQty
 * @returns {number}
 */
function getInventoryCap(product, bundleQty) {
  if (!product.available) {
    return 0;
  }

  if (product.inventory === null) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.floor(product.inventory / Math.max(bundleQty, 1));
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @returns {number}
 */
function getMaxBundleQty(selections, products) {
  let maxQty = Number.POSITIVE_INFINITY;
  let hasSelection = false;

  for (const product of products) {
    const selectedQty = selections[product.variantId] || 0;

    if (selectedQty <= 0) {
      continue;
    }

    hasSelection = true;

    if (product.inventory === null) {
      continue;
    }

    maxQty = Math.min(maxQty, Math.floor(product.inventory / selectedQty));
  }

  if (!hasSelection) {
    return 99;
  }

  if (!Number.isFinite(maxQty)) {
    return 99;
  }

  return Math.max(0, maxQty);
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @param {number} bundleQty
 * @param {number} requiredTotal
 * @returns {boolean}
 */
function canAddToCart(selections, products, bundleQty, requiredTotal) {
  if (bundleQty < 1) {
    return false;
  }

  if (getSelectedTotal(selections) !== requiredTotal) {
    return false;
  }

  if (bundleQty > getMaxBundleQty(selections, products)) {
    return false;
  }

  for (const product of products) {
    const selectedQty = selections[product.variantId] || 0;

    if (selectedQty <= 0) {
      continue;
    }

    const needed = selectedQty * bundleQty;

    if (!product.available) {
      return false;
    }

    if (product.inventory !== null && needed > product.inventory) {
      return false;
    }
  }

  return true;
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @returns {string}
 */
function buildBundleSelectionValue(selections, products) {
  /** @type {string[]} */
  const hashes = [];

  for (const product of products) {
    const qty = selections[product.variantId] || 0;

    for (let index = 0; index < qty; index += 1) {
      if (product.hash) {
        hashes.push(product.hash);
      }
    }
  }

  return hashes.join(' <> ');
}

/**
 * Human-readable pack mix for cart display (pipe-separated lines).
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @returns {string}
 */
function buildBundleContentsLabel(selections, products) {
  /** @type {string[]} */
  const parts = [];

  for (const product of products) {
    const qty = selections[product.variantId] || 0;

    if (qty <= 0 || !product.title) {
      continue;
    }

    parts.push(`${product.title} ×${qty}`);
  }

  return parts.join('|');
}

/**
 * @typedef {{ title: string, message: string, showViewCart?: boolean }} BundleErrorCopy
 */

/**
 * @param {unknown} error
 * @returns {string}
 */
function extractErrorDescription(error) {
  const candidate = Array.isArray(error) ? error[0] : error;

  if (typeof candidate === 'string') {
    return candidate.trim();
  }

  if (!candidate || typeof candidate !== 'object') {
    return '';
  }

  const record = /** @type {{ description?: string, message?: string }} */ (candidate);
  return record.description?.trim() || record.message?.trim() || '';
}

/**
 * @param {unknown} error
 * @returns {string | null}
 */
function findVariantIdInError(error) {
  const candidate = Array.isArray(error) ? error[0] : error;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const record = /** @type {{ id?: string | number, variant_id?: string | number, errors?: Record<string, unknown> }} */ (
    candidate
  );

  if (record.variant_id !== undefined && record.variant_id !== null) {
    return String(record.variant_id);
  }

  if (record.errors && typeof record.errors === 'object') {
    const variantIds = Object.keys(record.errors).filter((key) => /^\d+$/.test(key));

    if (variantIds.length === 1) {
      return variantIds[0];
    }
  }

  return null;
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @param {number} bundleQty
 * @returns {string | null}
 */
function findTightestInventoryProduct(selections, products, bundleQty) {
  let bestTitle = null;
  let worstGap = Number.POSITIVE_INFINITY;

  for (const product of products) {
    const qty = selections[product.variantId] || 0;

    if (qty <= 0 || product.inventory === null) {
      continue;
    }

    const needed = qty * bundleQty;
    const gap = product.inventory - needed;

    if (gap < worstGap) {
      worstGap = gap;
      bestTitle = product.title;
    }
  }

  return worstGap <= 0 ? bestTitle : null;
}


/**
 * @param {string} description
 * @param {BundleProduct[]} products
 * @returns {string | null}
 */
function findProductNameInError(description, products) {
  const lower = description.toLowerCase();

  for (const product of products) {
    const title = product.title?.trim();

    if (title && lower.includes(title.toLowerCase())) {
      return title;
    }
  }

  return null;
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @param {number} bundleQty
 * @param {string} bundleVariantId
 * @param {string} bundleTitle
 * @param {unknown} [error]
 * @returns {string | null}
 */
function findProductLabelForError(
  selections,
  products,
  bundleQty,
  bundleVariantId,
  bundleTitle,
  error
) {
  const description = extractErrorDescription(error);
  const namedInError = description ? findProductNameInError(description, products) : null;

  if (namedInError) {
    return namedInError;
  }

  if (bundleTitle && description.toLowerCase().includes(bundleTitle.toLowerCase())) {
    return bundleTitle;
  }

  const variantId = findVariantIdInError(error);

  if (variantId) {
    const matchedProduct = products.find((product) => product.variantId === variantId);

    if (matchedProduct?.title) {
      return matchedProduct.title;
    }
  }

  const stockNames = getStockIssueProductNames(selections, products, bundleQty);

  if (stockNames.length === 1) {
    return stockNames[0];
  }

  if (stockNames.length > 1) {
    return formatProductList(stockNames);
  }

  return findTightestInventoryProduct(selections, products, bundleQty);
}

/**
 * @param {unknown} error
 * @returns {'cart' | 'stock' | 'unknown'}
 */
function getApiIssueType(error) {
  const description = extractErrorDescription(error).toLowerCase();

  if (!description) {
    return 'unknown';
  }

  if (
    description.includes('maximum quantity') ||
    description.includes('already in your cart') ||
    description.includes("can't add more")
  ) {
    return 'cart';
  }

  if (
    description.includes('sold out') ||
    description.includes('out of stock') ||
    description.includes('not enough') ||
    description.includes('inventory')
  ) {
    return 'stock';
  }

  return 'unknown';
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @param {number} bundleQty
 * @returns {string[]}
 */
function getStockIssueProductNames(selections, products, bundleQty) {
  /** @type {string[]} */
  const names = [];

  for (const product of products) {
    const qty = selections[product.variantId] || 0;

    if (qty <= 0) {
      continue;
    }

    const needed = qty * bundleQty;

    if (!product.available || product.inventory === 0) {
      names.push(product.title);
      continue;
    }

    if (product.inventory !== null && needed > product.inventory) {
      names.push(product.title);
    }
  }

  return names;
}

/**
 * @param {string[]} names
 * @returns {string}
 */
function formatProductList(names) {
  if (names.length === 0) {
    return '';
  }

  if (names.length === 1) {
    return names[0];
  }

  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }

  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * @param {BundleProduct} product
 * @param {number} selectedQty
 * @param {number} bundleQty
 * @returns {BundleErrorCopy}
 */
function buildInventoryIssueError(product, selectedQty, bundleQty) {
  if (!product.available || product.inventory === 0) {
    return {
      title: 'Out of stock',
      message: `${product.title} is out of stock. Try a different pack mix.`,
    };
  }

  const inventory = product.inventory;

  if (inventory === null) {
    return {
      title: 'Not enough stock',
      message: `${product.title} doesn't have enough stock. Try a different pack mix.`,
    };
  }

  const maxBundles = Math.floor(inventory / Math.max(selectedQty, 1));

  if (maxBundles < 1) {
    return {
      title: 'Not enough stock',
      message: `Only ${inventory} ${product.title} left. Try a different pack mix.`,
    };
  }

  return {
    title: 'Not enough stock',
    message: `Only ${inventory} ${product.title} left — try ${maxBundles} bundle${
      maxBundles === 1 ? '' : 's'
    } or fewer, or a different pack mix.`,
  };
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @param {number} bundleQty
 * @returns {BundleErrorCopy[]}
 */
function getInventoryIssueErrors(selections, products, bundleQty) {
  /** @type {BundleErrorCopy[]} */
  const issues = [];

  for (const product of products) {
    const qty = selections[product.variantId] || 0;

    if (qty <= 0) {
      continue;
    }

    const needed = qty * bundleQty;

    if (!product.available) {
      issues.push(buildInventoryIssueError(product, qty, bundleQty));
      continue;
    }

    if (product.inventory === null || needed <= product.inventory) {
      continue;
    }

    issues.push(buildInventoryIssueError(product, qty, bundleQty));
  }

  return issues;
}

/**
 * @param {string} productLabel
 * @param {'stock' | 'cart' | 'low-stock'} issueType
 * @returns {BundleErrorCopy}
 */
function buildProductIssueError(productLabel, issueType = 'stock') {
  const isList = productLabel.includes(' and ') || productLabel.includes(', ');

  if (issueType === 'cart') {
    return {
      title: "Can't add more",
      message: isList
        ? `${productLabel} are maxed out in your cart. Try a different pack mix.`
        : `${productLabel} is maxed out in your cart. Try a different pack mix.`,
      showViewCart: true,
    };
  }

  if (issueType === 'low-stock') {
    return {
      title: 'Not enough stock',
      message: isList
        ? `${productLabel} don't have enough stock. Try fewer bundles or a different pack mix.`
        : `${productLabel} doesn't have enough stock. Try fewer bundles or a different pack mix.`,
    };
  }

  return {
    title: 'Out of stock',
    message: isList
      ? `${productLabel} are out of stock. Try a different pack mix.`
      : `${productLabel} is out of stock. Try a different pack mix.`,
  };
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @param {number} bundleQty
 * @param {string} bundleVariantId
 * @param {string} bundleTitle
 * @returns {Promise<BundleErrorCopy | null>}
 */
async function resolveCartMaxError(selections, products, bundleQty, bundleVariantId, bundleTitle) {
  try {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      return null;
    }

    const cart = /** @type {{ items?: Array<{ variant_id: number, quantity: number }> }} */ (
      await response.json()
    );

    const cartBundleQty = (cart.items || [])
      .filter((item) => String(item.variant_id) === bundleVariantId)
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

    const proposedBundleQty = cartBundleQty + Math.max(bundleQty, 1);
    /** @type {BundleErrorCopy[]} */
    const inventoryIssues = [];

    for (const product of products) {
      const selectedQty = selections[product.variantId] || 0;

      if (selectedQty <= 0) {
        continue;
      }

      const needed = selectedQty * proposedBundleQty;

      if (!product.available || product.inventory === 0) {
        inventoryIssues.push(buildInventoryIssueError(product, selectedQty, proposedBundleQty));
        continue;
      }

      if (product.inventory !== null && needed > product.inventory) {
        inventoryIssues.push(buildInventoryIssueError(product, selectedQty, proposedBundleQty));
      }
    }

    if (inventoryIssues.length === 1) {
      return inventoryIssues[0];
    }

    if (inventoryIssues.length > 1) {
      const stockNames = getStockIssueProductNames(selections, products, proposedBundleQty);

      return {
        title: 'Not enough stock',
        message: `${formatProductList(stockNames)} don't have enough stock for this many bundles. Try fewer bundles or a different pack mix.`,
      };
    }

    if (cartBundleQty > 0 && bundleTitle) {
      return {
        title: "Can't add more",
        message: `${bundleTitle} is maxed out in your cart. Try a different pack mix.`,
        showViewCart: true,
      };
    }

    return null;
  } catch {
    return null;
  }
}


/**
 * @param {unknown} error
 * @param {{
 *   selections?: Record<string, number>,
 *   products?: BundleProduct[],
 *   bundleQty?: number,
 *   bundleVariantId?: string,
 *   bundleTitle?: string,
 * }} [context]
 * @returns {BundleErrorCopy | null}
 */
function parseCartAddError(error, context = {}) {
  const selections = context.selections || {};
  const products = context.products || [];
  const bundleQty = context.bundleQty || 1;
  const bundleVariantId = context.bundleVariantId || '';
  const bundleTitle = context.bundleTitle || '';
  const description = extractErrorDescription(error);
  const issueType = getApiIssueType(error);

  if (!description) {
    return null;
  }

  const productLabel = findProductLabelForError(
    selections,
    products,
    bundleQty,
    bundleVariantId,
    bundleTitle,
    error
  );

  if (issueType === 'cart') {
    // Prefer flavor inventory messaging over naming the parent bundle — Shopify often
    // reports component stock limits as a cart-max on the bundle line item.
    const tightest = findTightestInventoryProduct(selections, products, bundleQty);

    if (tightest) {
      const product = products.find((entry) => entry.title === tightest);

      if (product) {
        return buildInventoryIssueError(product, selections[product.variantId] || 1, bundleQty);
      }
    }

    if (productLabel && productLabel !== bundleTitle) {
      return buildProductIssueError(productLabel, 'cart');
    }

    return {
      title: "Can't add more",
      message: 'This item is maxed out in your cart. Try a different pack mix.',
      showViewCart: true,
    };
  }

  if (issueType === 'stock') {
    if (productLabel) {
      const matchedProduct = products.find(
        (product) => product.title && productLabel.includes(product.title)
      );

      if (matchedProduct) {
        const selectedQty = selections[matchedProduct.variantId] || 1;
        const needed = selectedQty * bundleQty;

        if (
          matchedProduct.available &&
          matchedProduct.inventory !== null &&
          matchedProduct.inventory > 0 &&
          needed > matchedProduct.inventory
        ) {
          return buildInventoryIssueError(matchedProduct, selectedQty, bundleQty);
        }

        if (!matchedProduct.available || matchedProduct.inventory === 0) {
          return buildProductIssueError(productLabel, 'stock');
        }

        return buildProductIssueError(productLabel, 'low-stock');
      }

      return buildProductIssueError(productLabel, 'low-stock');
    }

    return {
      title: 'Not enough stock',
      message: 'Try fewer bundles or a different pack mix.',
    };
  }

  return {
    title: "Can't add bundle",
    message: 'Try a different pack mix.',
  };
}

/**
 * Toast copy for incomplete selection (desktop fallback / non-mobile).
 * @param {number} selectedTotal
 * @param {number} requiredTotal
 * @returns {string | null}
 */
function getIncompleteSelectionMessage(selectedTotal, requiredTotal) {
  if (selectedTotal < requiredTotal) {
    const remaining = requiredTotal - selectedTotal;
    return `Add ${remaining} more pack${remaining === 1 ? '' : 's'}.`;
  }

  if (selectedTotal > requiredTotal) {
    const extra = selectedTotal - requiredTotal;
    return `Remove ${extra} pack${extra === 1 ? '' : 's'}.`;
  }

  return null;
}

/**
 * Inline validation under Total — Figma 1323:3762 / 1325:3439.
 * @param {number} selectedTotal
 * @param {number} requiredTotal
 * @returns {string | null}
 */
function getInlineIncompleteMessage(selectedTotal, requiredTotal) {
  if (selectedTotal < requiredTotal) {
    const remaining = requiredTotal - selectedTotal;
    return remaining === 1
      ? 'Please select one more flavor.'
      : `Please select ${remaining} more flavors.`;
  }

  if (selectedTotal > requiredTotal) {
    const extra = selectedTotal - requiredTotal;
    return extra === 1
      ? 'Please remove one flavor.'
      : `Please remove ${extra} flavors.`;
  }

  return null;
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isElementInViewport(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const headerOffset = Math.min(120, viewportHeight * 0.15);

  return rect.top >= headerOffset && rect.bottom <= viewportHeight;
}

/**
 * Scroll the bundle picker into view when the validation message is off-screen
 * (typical on mobile when ATC sits below the fold).
 * @param {HTMLElement} mount
 * @param {HTMLElement | null} [focusTarget]
 */
function scrollToBundleBuilder(mount, focusTarget = null) {
  const card = mount.querySelector('.topeak-simple-bundle__card');
  const target = card instanceof HTMLElement ? card : mount;

  const runScroll = () => {
    if (!isElementInViewport(target)) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
    }
  };

  // Wait a frame so the incomplete message is in layout before scrolling.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(runScroll);
  });
}

/**
 * @param {Record<string, number>} selections
 * @param {BundleProduct[]} products
 * @param {number} bundleQty
 * @param {number} requiredTotal
 * @param {unknown} [apiError]
 * @param {string} [bundleVariantId]
 * @param {string} [bundleTitle]
 * @returns {BundleErrorCopy}
 */
function getBundleAddErrorMessage(
  selections,
  products,
  bundleQty,
  requiredTotal,
  apiError,
  bundleVariantId = '',
  bundleTitle = ''
) {
  const selectedTotal = getSelectedTotal(selections);
  const incompleteMessage = getIncompleteSelectionMessage(selectedTotal, requiredTotal);

  if (incompleteMessage) {
    return {
      title: 'Complete your bundle',
      message: incompleteMessage,
    };
  }

  const inventoryIssues = getInventoryIssueErrors(selections, products, bundleQty);

  if (inventoryIssues.length === 1) {
    return inventoryIssues[0];
  }

  if (inventoryIssues.length > 1) {
    const allOutOfStock = inventoryIssues.every((issue) => issue.title === 'Out of stock');
    const stockNames = getStockIssueProductNames(selections, products, bundleQty);

    if (allOutOfStock) {
      return buildProductIssueError(formatProductList(stockNames), 'stock');
    }

    return {
      title: 'Not enough stock',
      message: `${formatProductList(stockNames)} don't have enough stock. Try fewer bundles or a different pack mix.`,
    };
  }

  const parsedApiMessage = parseCartAddError(apiError, {
    selections,
    products,
    bundleQty,
    bundleVariantId,
    bundleTitle,
  });

  if (parsedApiMessage) {
    return parsedApiMessage;
  }

  return {
    title: "Can't add bundle",
    message: 'Try a different pack mix.',
  };
}

/**
 * @param {BundleErrorCopy} error
 */
function showBundleError(error) {
  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartError, {
      bubbles: true,
      detail: {
        data: {
          title: error.title,
          message: error.message,
          showViewCart: error.showViewCart === true,
        },
      },
    })
  );
}

/**
 * @param {HTMLElement} section
 * @param {Record<string, number>} selections
 * @param {number} bundleQty
 * @param {BundleConfig} config
 */
function syncBundleState(section, selections, bundleQty, config) {
  bundleStateBySection.set(section, { selections, bundleQty, config });
}

/**
 * @param {HTMLElement} section
 * @param {boolean} isLoading
 */
function setAddButtonLoading(section, isLoading) {
  const button = section.querySelector('[data-topeak-simple-bundle-add]');

  if (!(button instanceof HTMLButtonElement)) {
    return;
  }

  const label = button.querySelector('[data-topeak-simple-bundle-add-label]');

  if (isLoading) {
    if (!button.dataset.idleLabel && label) {
      button.dataset.idleLabel = label.textContent || '';
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add(HERO_ADD_LOADING_CLASS);

    if (label) {
      label.textContent = (button.dataset.loadingLabel || 'Adding...').toUpperCase();
    }

    return;
  }

  button.classList.remove(HERO_ADD_LOADING_CLASS);
  button.removeAttribute('aria-busy');
  button.disabled = false;

  if (label && button.dataset.idleLabel) {
    label.textContent = button.dataset.idleLabel;
    delete button.dataset.idleLabel;
  }
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/**
 * @param {HTMLElement} mount
 * @param {HTMLElement} section
 * @param {BundleConfig} config
 */
function renderBundleBuilder(mount, section, config) {
  const addLabel = section.dataset.addLabel || 'Add to cart';
  const moneyFormat = section.dataset.moneyFormat || '';
  const unitPriceCents = Number(section.dataset.unitPriceCents) || 0;
  const minusIcon = section.dataset.minusIcon || '';
  const plusIcon = section.dataset.plusIcon || '';
  const restoreIcon = section.dataset.restoreIcon || '';
  const infoIcon = section.dataset.infoIcon || '';

  /** @type {Record<string, number>} */
  const selections = Object.fromEntries(config.products.map((product) => [product.variantId, 0]));
  let bundleQty = 1;

  mount.innerHTML = `
    <div class="topeak-simple-bundle__card" data-figma-node="1325:3389">
      <div class="topeak-simple-bundle__products" role="group" aria-label="Choose bundle items">
        ${config.products
          .map(
            (product) => `
              <article class="topeak-simple-bundle__product" data-variant-id="${product.variantId}">
                <div class="topeak-simple-bundle__product-media">
                  ${
                    product.imageUrl
                      ? `<img src="${product.imageUrl}" alt="" class="topeak-simple-bundle__product-image" width="66" height="88" loading="lazy">`
                      : ''
                  }
                </div>
                <div class="topeak-simple-bundle__product-body">
                  <p class="topeak-simple-bundle__product-title">${escapeHtml(product.title)}</p>
                  <div class="topeak-simple-bundle__qty" data-simple-bundle-qty="${product.variantId}">
                    <button type="button" class="topeak-simple-bundle__qty-btn topeak-simple-bundle__qty-btn--minus" data-action="decrease" aria-label="Decrease ${escapeHtml(product.title)} quantity">
                      <span class="visually-hidden">Decrease quantity</span>
                    </button>
                    <span class="topeak-simple-bundle__qty-value" aria-live="polite" data-qty-display>0</span>
                    <button type="button" class="topeak-simple-bundle__qty-btn topeak-simple-bundle__qty-btn--plus" data-action="increase" aria-label="Increase ${escapeHtml(product.title)} quantity">
                      <span class="visually-hidden">Increase quantity</span>
                    </button>
                  </div>
                </div>
              </article>
            `
          )
          .join('')}
      </div>
      <div class="topeak-simple-bundle__divider" aria-hidden="true"></div>
      <div class="topeak-simple-bundle__summary">
        <div class="topeak-simple-bundle__total">
          <p class="topeak-simple-bundle__total-label">Total</p>
          <p class="topeak-simple-bundle__total-value"><span data-total-count>0</span> Packs</p>
          <p
            class="topeak-simple-bundle__incomplete"
            data-incomplete-message
            data-figma-node="1323:3762"
            role="alert"
            tabindex="-1"
          ></p>
        </div>
        <button type="button" class="topeak-simple-bundle__reset" data-action="reset">
          <span class="topeak-simple-bundle__reset-icon" aria-hidden="true"></span>
          <span>Reset</span>
        </button>
      </div>
    </div>
    <div class="topeak-simple-bundle__actions">
      <div class="topeak-simple-bundle__bundle-qty" data-simple-bundle-bundle-qty>
        <button type="button" class="topeak-simple-bundle__bundle-qty-btn topeak-simple-bundle__bundle-qty-btn--minus" data-action="bundle-decrease" aria-label="Decrease bundle quantity">
          <span class="visually-hidden">Decrease bundle quantity</span>
        </button>
        <span class="topeak-simple-bundle__bundle-qty-value" aria-live="polite" data-bundle-qty-display>1</span>
        <button type="button" class="topeak-simple-bundle__bundle-qty-btn topeak-simple-bundle__bundle-qty-btn--plus" data-action="bundle-increase" aria-label="Increase bundle quantity">
          <span class="visually-hidden">Increase bundle quantity</span>
        </button>
      </div>
      <button
        type="button"
        class="topeak-btn topeak-btn--primary topeak-btn--product-hero-add topeak-simple-bundle__add"
        data-topeak-simple-bundle-add
        data-loading-label="Adding..."
      >
        <span data-topeak-simple-bundle-add-label>${addLabel.toUpperCase()} - ${formatMoney(unitPriceCents, moneyFormat)}</span>
      </button>
    </div>
    <p class="topeak-simple-bundle__hint">
      <span class="topeak-simple-bundle__hint-icon" aria-hidden="true"></span>
      <span>Each bundle contains the ${config.itemCount} packs you selected above.</span>
    </p>
  `;

  const root = mount.closest('.topeak-product-hero');

  if (root instanceof HTMLElement) {
    if (minusIcon) {
      root.style.setProperty('--topeak-simple-bundle-icon-minus', `url("${minusIcon}")`);
    }

    if (plusIcon) {
      root.style.setProperty('--topeak-simple-bundle-icon-plus', `url("${plusIcon}")`);
    }

    if (restoreIcon) {
      root.style.setProperty('--topeak-simple-bundle-icon-restore', `url("${restoreIcon}")`);
    }

    if (infoIcon) {
      root.style.setProperty('--topeak-simple-bundle-icon-info', `url("${infoIcon}")`);
    }
  }

  const totalCountEl = mount.querySelector('[data-total-count]');
  const incompleteMessageEl = mount.querySelector('[data-incomplete-message]');
  const bundleQtyDisplay = mount.querySelector('[data-bundle-qty-display]');
  const addButton = mount.querySelector('[data-topeak-simple-bundle-add]');
  const addLabelEl = mount.querySelector('[data-topeak-simple-bundle-add-label]');

  /** Shown after an incomplete add-to-cart attempt until the selection is fixed. */
  let incompletePrompted = false;

  /**
   * @param {number} selectedTotal
   * @param {{ scroll?: boolean, focus?: boolean }} [options]
   */
  const syncIncompleteMessage = (selectedTotal, options = {}) => {
    if (!(incompleteMessageEl instanceof HTMLElement)) {
      return;
    }

    const message = getInlineIncompleteMessage(selectedTotal, config.itemCount);
    const shouldShow = Boolean(incompletePrompted && message);

    if (shouldShow && message) {
      incompleteMessageEl.textContent = message;
      incompleteMessageEl.classList.add('topeak-simple-bundle__incomplete--visible');
      incompleteMessageEl.setAttribute('aria-hidden', 'false');

      if (options.scroll || options.focus) {
        scrollToBundleBuilder(mount, options.focus ? incompleteMessageEl : null);
      }

      return;
    }

    incompleteMessageEl.textContent = '';
    incompleteMessageEl.classList.remove('topeak-simple-bundle__incomplete--visible');
    incompleteMessageEl.setAttribute('aria-hidden', 'true');
  };

  /**
   * @param {number} selectedTotal
   */
  const showIncompleteInline = (selectedTotal) => {
    incompletePrompted = true;
    syncIncompleteMessage(selectedTotal, { scroll: true, focus: true });
  };

  const updateUi = () => {
    const selectedTotal = getSelectedTotal(selections);
    const maxBundleQty = getMaxBundleQty(selections, config.products);

    syncBundleState(section, selections, bundleQty, config);

    if (bundleQty > maxBundleQty) {
      bundleQty = Math.max(1, maxBundleQty);
    }

    if (totalCountEl) {
      totalCountEl.textContent = String(selectedTotal);
    }

    if (bundleQtyDisplay) {
      bundleQtyDisplay.textContent = String(bundleQty);
    }

    for (const product of config.products) {
      const qty = selections[product.variantId] || 0;
      const productEl = mount.querySelector(`[data-variant-id="${product.variantId}"]`);
      const qtyDisplay = productEl?.querySelector('[data-qty-display]');
      const decreaseBtn = productEl?.querySelector('[data-action="decrease"]');
      const increaseBtn = productEl?.querySelector('[data-action="increase"]');
      const inventoryCap = getInventoryCap(product, bundleQty);
      const canIncrease =
        selectedTotal < config.itemCount && qty < inventoryCap && product.available;

      if (qtyDisplay) {
        qtyDisplay.textContent = String(qty);
      }

      if (decreaseBtn instanceof HTMLButtonElement) {
        decreaseBtn.disabled = qty <= 0;
      }

      if (increaseBtn instanceof HTMLButtonElement) {
        increaseBtn.disabled = !canIncrease;
      }
    }

    const bundleDecrease = mount.querySelector('[data-action="bundle-decrease"]');
    const bundleIncrease = mount.querySelector('[data-action="bundle-increase"]');

    if (bundleDecrease instanceof HTMLButtonElement) {
      bundleDecrease.disabled = bundleQty <= 1;
    }

    if (bundleIncrease instanceof HTMLButtonElement) {
      bundleIncrease.disabled = bundleQty >= maxBundleQty;
    }

    const ready = canAddToCart(selections, config.products, bundleQty, config.itemCount);

    if (ready) {
      incompletePrompted = false;
    }

    syncIncompleteMessage(selectedTotal);

    // Never disable for incomplete packs — click must show Figma 1323:3762 + scroll.
    if (addButton instanceof HTMLButtonElement) {
      const isLoading = addButton.classList.contains(HERO_ADD_LOADING_CLASS);
      addButton.disabled = isLoading;
      addButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    }

    if (addLabelEl) {
      addLabelEl.textContent = `${addLabel.toUpperCase()} - ${formatMoney(unitPriceCents * bundleQty, moneyFormat)}`;
    }
  };

  mount.addEventListener('click', (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const addTrigger = target.closest('[data-topeak-simple-bundle-add]');

    if (addTrigger instanceof HTMLButtonElement) {
      if (addTrigger.disabled || addTrigger.classList.contains(HERO_ADD_LOADING_CLASS)) {
        return;
      }

      const selectedTotal = getSelectedTotal(selections);

      // Incomplete pack count: Figma 1323:3762 inline message + scroll (no toast).
      if (selectedTotal !== config.itemCount) {
        event.preventDefault();
        showIncompleteInline(selectedTotal);
        return;
      }

      if (!canAddToCart(selections, config.products, bundleQty, config.itemCount)) {
        showBundleError(
          getBundleAddErrorMessage(
            selections,
            config.products,
            bundleQty,
            config.itemCount,
            undefined,
            config.bundleVariantId,
            config.bundleTitle
          )
        );
        return;
      }

      const selectionValue = buildBundleSelectionValue(selections, config.products);
      const contentsValue = buildBundleContentsLabel(selections, config.products);
      const merchandiseId = `gid://shopify/ProductVariant/${config.bundleVariantId}`;

      setAddButtonLoading(section, true);

      document.dispatchEvent(
        new CustomEvent('simple-bundles-byob:add-to-cart', {
          bubbles: true,
          cancelable: true,
          detail: {
            items: [
              {
                merchandiseId,
                quantity: bundleQty,
                attributes: [
                  { key: '_bundle_selection', value: selectionValue },
                  { key: '_bundle_contents', value: contentsValue },
                ],
              },
            ],
            variantId: config.bundleVariantId,
          },
        })
      );
      return;
    }

    const button = target.closest('[data-action]');

    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    const action = button.getAttribute('data-action');
    const selectedTotal = getSelectedTotal(selections);

    if (action === 'reset') {
      for (const product of config.products) {
        selections[product.variantId] = 0;
      }

      bundleQty = 1;
      incompletePrompted = false;
      updateUi();
      return;
    }

    if (action === 'bundle-decrease') {
      bundleQty = Math.max(1, bundleQty - 1);
      updateUi();
      return;
    }

    if (action === 'bundle-increase') {
      const maxBundleQty = getMaxBundleQty(selections, config.products);

      if (bundleQty < maxBundleQty) {
        bundleQty += 1;
      }

      updateUi();
      return;
    }

    const qtyWrap = button.closest('[data-simple-bundle-qty]');
    const variantId = qtyWrap?.getAttribute('data-simple-bundle-qty');
    const product = config.products.find((entry) => entry.variantId === variantId);

    if (!product || !variantId) {
      return;
    }

    const currentQty = selections[variantId] || 0;

    if (action === 'decrease') {
      selections[variantId] = Math.max(0, currentQty - 1);
      updateUi();
      return;
    }

    if (action === 'increase') {
      const inventoryCap = getInventoryCap(product, bundleQty);

      if (selectedTotal >= config.itemCount || currentQty >= inventoryCap) {
        return;
      }

      selections[variantId] = currentQty + 1;
      updateUi();
    }
  });

  updateUi();
}

/**
 * Visually hide native Simple Bundles BYOB chrome after config is parsed.
 * Keeps nodes in the DOM (attributes still readable) but removes storefront branding.
 * @param {Element} byobEl
 * @param {Element | null} appBlock
 * @param {Element | null} byobEmbed
 */
function hideNativeByobChrome(byobEl, appBlock, byobEmbed) {
  const targets = [byobEl, appBlock, byobEmbed];

  for (const target of targets) {
    if (!(target instanceof HTMLElement)) {
      continue;
    }

    target.hidden = true;
    target.setAttribute('aria-hidden', 'true');
    // App embed CSS often forces display; beat it after parse.
    target.style.setProperty('display', 'none', 'important');
  }
}

/**
 * @param {HTMLElement} section
 * @param {HTMLElement} mount
 * @param {Element} byobEl
 * @param {BundleConfig} config
 */
function activateSimpleBundle(section, mount, byobEl, config) {
  const heroVariantId = section.dataset.bundleVariantId;

  if (heroVariantId && heroVariantId !== config.bundleVariantId) {
    return;
  }

  if (mount.dataset.simpleBundleReady === 'true') {
    return;
  }

  const standardBuy = section.querySelector(STANDARD_BUY_SELECTOR);
  const appBlock = byobEl.closest('.shopify-app-block');
  const shopifySection = section.closest('.shopify-section');
  const byobEmbed = document.getElementById('byob-app-embed-wrapper');

  section.classList.add('topeak-product-hero--simple-bundle');
  mount.hidden = false;
  mount.removeAttribute('aria-hidden');
  mount.dataset.simpleBundleReady = 'true';

  if (shopifySection instanceof HTMLElement) {
    shopifySection.classList.add('topeak-product-hero-section--simple-bundle-active');
    shopifySection.style.display = '';
  }

  if (standardBuy instanceof HTMLElement) {
    standardBuy.hidden = true;
    standardBuy.setAttribute('aria-hidden', 'true');
  }

  // Hide native Simple Bundles UI + "Powered by Simple Bundles" (app block and/or app embed).
  hideNativeByobChrome(byobEl, appBlock, byobEmbed);

  renderBundleBuilder(mount, section, config);
  protectProductHeroVisibility(section);
}

/**
 * @param {HTMLElement} section
 */
function bindSimpleBundleEvents(section) {
  if (section.dataset.simpleBundleBound === 'true') {
    return;
  }

  section.dataset.simpleBundleBound = 'true';

  document.addEventListener('simple-bundles-byob:add-to-cart:complete', () => {
    setAddButtonLoading(section, false);
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: {
          data: {
            source: 'topeak-simple-bundle',
            didError: false,
          },
        },
      })
    );
  });

  document.addEventListener('simple-bundles-byob:add-to-cart:error', async (event) => {
    setAddButtonLoading(section, false);

    const detail = /** @type {{ errors?: unknown[] }} */ (event).detail || {};
    const state = bundleStateBySection.get(section);
    let error = state
      ? getBundleAddErrorMessage(
          state.selections,
          state.config.products,
          state.bundleQty,
          state.config.itemCount,
          detail.errors?.[0],
          state.config.bundleVariantId,
          state.config.bundleTitle
        )
      : parseCartAddError(detail.errors?.[0]) || {
          title: "Can't add bundle",
          message: 'Try a different pack mix.',
        };

    if (state && getApiIssueType(detail.errors?.[0]) === 'cart') {
      const resolved = await resolveCartMaxError(
        state.selections,
        state.config.products,
        state.bundleQty,
        state.config.bundleVariantId,
        state.config.bundleTitle
      );

      if (resolved) {
        error = resolved;
      }
    }

    showBundleError(error);
  });
}

/**
 * @param {Element} byobEl
 */
function tryInitialize(byobEl) {
  const config = parseByobBundle(byobEl);

  if (!config) {
    return;
  }

  for (const section of document.querySelectorAll(SECTION_SELECTOR)) {
    if (!(section instanceof HTMLElement)) {
      continue;
    }

    const mount = section.querySelector(MOUNT_SELECTOR);

    if (!(mount instanceof HTMLElement)) {
      continue;
    }

    bindSimpleBundleEvents(section);
    activateSimpleBundle(section, mount, byobEl, config);
  }
}

function watchForByobBundle() {
  const existing = document.querySelector(BYOB_SELECTOR);

  if (existing) {
    tryInitialize(existing);
    return;
  }

  const observer = new MutationObserver(() => {
    const byobEl = document.querySelector(BYOB_SELECTOR);

    if (byobEl) {
      observer.disconnect();
      tryInitialize(byobEl);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * @param {HTMLElement} section
 */
function protectProductHeroVisibility(section) {
  const shopifySection = section.closest('.shopify-section');

  if (!(shopifySection instanceof HTMLElement)) {
    return;
  }

  const observer = new MutationObserver(() => {
    if (shopifySection.style.display === 'none') {
      shopifySection.style.display = '';
    }
  });

  observer.observe(shopifySection, { attributes: true, attributeFilter: ['style'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchForByobBundle, { once: true });
} else {
  watchForByobBundle();
}
